import {parseRef} from '@wandb/weave/react';
import React from 'react';

import {SmallRef} from '../../../smallRef/SmallRef';
import {formatScoreValue} from './evaluationUtils';
import {BooleanSummary, NumericSummary, ScorerRefMap, ScoreTree} from './types';

type ScoreTreeViewProps = {
  tree: ScoreTree;
  path: string[];
  scorerRefMap: ScorerRefMap;
};

const isNumericSummary = (value: unknown): value is NumericSummary => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'mean' in value &&
    typeof (value as NumericSummary).mean === 'number' &&
    Object.keys(value).length === 1
  );
};

const isBooleanSummary = (value: unknown): value is BooleanSummary => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'true_count' in value &&
    'true_fraction' in value &&
    typeof (value as BooleanSummary).true_count === 'number' &&
    typeof (value as BooleanSummary).true_fraction === 'number' &&
    Object.keys(value).length === 2
  );
};

type GroupedEntries = {
  numericSummaries: Array<[string, NumericSummary]>;
  booleanSummaries: Array<[string, BooleanSummary]>;
  legacyLeaves: Array<[string, number | boolean]>;
  nestedTrees: Array<[string, ScoreTree]>;
};

const groupEntriesByType = (
  entries: Array<[string, ScoreTree[string]]>,
  scorerRefMap: ScorerRefMap
): GroupedEntries => {
  const grouped: GroupedEntries = {
    numericSummaries: [],
    booleanSummaries: [],
    legacyLeaves: [],
    nestedTrees: [],
  };

  entries.forEach(([key, value]) => {
    if (isNumericSummary(value)) {
      grouped.numericSummaries.push([key, value]);
    } else if (isBooleanSummary(value)) {
      grouped.booleanSummaries.push([key, value]);
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      grouped.legacyLeaves.push([key, value]);
    } else {
      grouped.nestedTrees.push([key, value as ScoreTree]);
    }
  });

  return grouped;
};

export const ScoreTreeView: React.FC<ScoreTreeViewProps> = ({
  tree,
  path,
  scorerRefMap,
}) => {
  const entries = Object.entries(tree);
  const filteredEntries =
    path.length === 0
      ? entries.filter(([key, value]) => {
          if (Boolean(scorerRefMap[key])) {
            return true;
          }
          if (isNumericSummary(value) || isBooleanSummary(value)) {
            return true;
          }
          if (typeof value === 'object' && value !== null) {
            const hasAnySummaryDescendant = (node: any): boolean => {
              if (isNumericSummary(node) || isBooleanSummary(node)) {
                return true;
              }
              if (typeof node === 'object' && node !== null) {
                return Object.values(node).some(hasAnySummaryDescendant);
              }
              return false;
            };
            return hasAnySummaryDescendant(value);
          }
          return false;
        })
      : entries;
  const grouped = groupEntriesByType(filteredEntries, scorerRefMap);
  const isTopLevel = path.length === 0;

  // Check if there's any content to render
  const hasContent =
    grouped.booleanSummaries.length > 0 ||
    grouped.numericSummaries.length > 0 ||
    grouped.legacyLeaves.length > 0 ||
    grouped.nestedTrees.length > 0;

  // If at top level and nothing to render, show empty state
  if (isTopLevel && !hasContent) {
    return (
      <div className="py-16 text-center text-sm italic text-moon-500">
        No scores available.
      </div>
    );
  }

  const renderLeaf = (
    key: string,
    value: number | boolean | NumericSummary | BooleanSummary
  ) => {
    let valueDisplay: React.ReactNode;
    let annotationDisplay: React.ReactNode = (
      <span className="text-sm text-moon-500" />
    );

    if (isNumericSummary(value)) {
      valueDisplay = (
        <span className="text-right text-moon-900">
          {formatScoreValue(value.mean)}
        </span>
      );
      annotationDisplay = <span className="text-sm text-moon-500">(avg)</span>;
    } else if (isBooleanSummary(value)) {
      const percentage = (value.true_fraction * 100).toFixed(1);
      // Calculate the total number of items in the dataset.
      const total =
        value.true_fraction > 0
          ? Math.round(value.true_count / value.true_fraction)
          : value.true_count;
      valueDisplay = (
        <span className="inline-flex items-baseline justify-end gap-1 text-right text-moon-900">
          <span>{percentage}</span>
          <span>%</span>
        </span>
      );
      annotationDisplay = (
        <span className="text-sm text-moon-500">
          {value.true_count} of {total}
        </span>
      );
    } else {
      valueDisplay = (
        <span className="text-right text-moon-900">
          {formatScoreValue(value)}
        </span>
      );
    }

    return (
      <div key={key} className="flex items-center gap-4 py-4 pr-6 text-sm">
        <div className="min-w-[180px] text-moon-700">
          {isTopLevel && scorerRefMap[key] ? (
            <SmallRef objRef={parseRef(scorerRefMap[key])} noLink />
          ) : (
            <span className="text-moon-800">{key}</span>
          )}
        </div>
        <div className="ml-auto flex items-center">{annotationDisplay}</div>
        <div className="flex items-center justify-end text-xs font-mono min-w-[72px]">
          {valueDisplay}
        </div>
      </div>
    );
  };

  // TODO: Validate the indenting calculation since we changes it here.
  // TODO: If this is ok, we can probably use tailwind for the below.
  // const indentPx = path.length > 0 ? Math.min(48, 12 + path.length * 10) : 0;

  return (
    <div
      className={path.length > 0 ? 'border-l border-moon-200 pl-16' : 'pl-0'}
      style={path.length > 0 ? {marginLeft: 11} : {paddingTop: 0}}>
      {grouped.booleanSummaries.length > 0 &&
        grouped.booleanSummaries.map(([key, value]) => renderLeaf(key, value))}

      {/* Render numeric summaries second */}
      {grouped.numericSummaries.length > 0 &&
        grouped.numericSummaries.map(([key, value]) => renderLeaf(key, value))}

      {/* Render legacy leaves third */}
      {grouped.legacyLeaves.length > 0 &&
        grouped.legacyLeaves.map(([key, value]) => renderLeaf(key, value))}

      {/* Render nested trees last */}
      {grouped.nestedTrees.map(([key, value]) => {
        const fullPath = [...path, key];
        return (
          <div key={key} className="mb-3">
            {isTopLevel && scorerRefMap[key] ? (
              <div className="mb-2 py-1.5 pr-6 text-sm font-semibold text-moon-900">
                <SmallRef objRef={parseRef(scorerRefMap[key])} noLink />
              </div>
            ) : (
              <div className="mb-1 py-1.5 pr-6 text-sm font-semibold text-moon-900">
                {key}
              </div>
            )}
            <ScoreTreeView
              tree={value}
              path={fullPath}
              scorerRefMap={scorerRefMap}
            />
          </div>
        );
      })}
    </div>
  );
};
